/**
 * ChatDetails — the screen behind the chat header, mirroring mobile's
 * ChatProfileDetails: identity, View Profile, chat settings and safety actions.
 *
 * Group conversations are chat-only entities with no member profile behind the
 * title, so they show the member roster instead of View Profile.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { useMessages } from '@wlb/shared/context/MessageContext';
import { useProfile } from '@wlb/shared/context/ProfileContext';
import { ToastService } from '@wlb/shared/platform/notifier';
import { isAiConversationType, isGroupConversationType } from '@wlb/shared/constants';
import { normalizeUserGuid } from '@wlb/shared/utils/navigateToUserProfile';
import { getUserFacingErrorMessage } from '@wlb/shared/utils/apiErrorHandler';
import type { ConversationMember } from '@wlb/shared/types/chat';
import {
  AlertCircleIcon,
  BlockIcon,
  BuddiesIcon,
  ChevronLeftIcon,
  TrashIcon,
} from '../components/ui/icons';
import { Avatar } from '../components/ui/Avatar';
import { Button } from '../components/ui/Button';
import { ListStatus } from '../components/ui/ListStatus';
import { Skeleton, SkeletonGroup } from '../components/ui/Skeleton';
import { ConfirmModal } from '../features/feed/ConfirmModal';
import { ReportUserModal } from '../features/profile/ReportUserModal';
import { GroupAvatar } from '../features/messages/GroupAvatar';
import { resolveAvatarSrc } from '../features/messages/avatarSource';
import { resolvePeerUserGuid } from '../features/messages/resolvePeerUserGuid';
import { useOpenUserProfile } from '../features/profile/useOpenUserProfile';
import { useAuthGate } from '../components/feedback/AuthGate';
import { getUsernameDisplay } from '../utils/usernameDisplay';
import '../features/messages/messages.css';

type Prompt = 'block' | 'delete';

export function ChatDetails() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { conversationId } = useParams<{ conversationId: string }>();
  const { profile } = useProfile();
  const openUserProfile = useOpenUserProfile();
  const { requireAuth } = useAuthGate();
  const {
    loading,
    getConversationById,
    getParticipants,
    fetchConversations,
    blockConversation,
    deleteConversation,
  } = useMessages();

  const [prompt, setPrompt] = useState<Prompt | null>(null);
  const [busy, setBusy] = useState(false);
  const [reportGuid, setReportGuid] = useState<string | null>(null);
  const [resolvingPeer, setResolvingPeer] = useState(false);
  const [requestedList, setRequestedList] = useState(false);
  const [fetchedMembers, setFetchedMembers] = useState<ConversationMember[] | null>(null);
  const [membersError, setMembersError] = useState<string | null>(null);

  const conversation = useMemo(
    () => getConversationById(conversationId || ''),
    [getConversationById, conversationId],
  );

  const isGroup =
    conversation?.category === 'groups' || isGroupConversationType(conversation?.conversationType);
  const isAi = isAiConversationType(conversation?.conversationType);
  const displayName =
    (isGroup
      ? conversation?.groupName || conversation?.name
      : getUsernameDisplay(conversation)) || '';
  const headerTitle = displayName || t(isGroup ? 'chat.unnamedGroup' : 'chat.defaultTitle');
  const avatarSrc = resolveAvatarSrc(conversation?.avatar);
  const selfUserId = profile?.userId != null ? String(profile.userId) : null;

  // A details deep link can land before the inbox has ever been fetched.
  useEffect(() => {
    if (conversation || requestedList) return;
    setRequestedList(true);
    fetchConversations({ refresh: true }).catch(() => undefined);
  }, [conversation, requestedList, fetchConversations]);

  // Inbox payloads often omit participants, so the roster is fetched rather
  // than rendered empty.
  useEffect(() => {
    if (!isGroup || !conversationId) return;
    if (conversation?.members?.length || fetchedMembers || membersError) return;
    let cancelled = false;
    getParticipants(conversationId)
      .then(members => { if (!cancelled) setFetchedMembers(members); })
      // A roster that could not be fetched is not a group without members.
      .catch((err: unknown) => {
        if (!cancelled) {
          setMembersError(getUserFacingErrorMessage(err, t('common.errors.generic')));
        }
      });
    return () => { cancelled = true; };
  }, [
    isGroup,
    conversationId,
    conversation?.members?.length,
    fetchedMembers,
    membersError,
    getParticipants,
    t,
  ]);

  const retryMembers = useCallback(() => {
    setMembersError(null);
    setFetchedMembers(null);
  }, []);

  // Robi threads have their own screen, exactly as the chat page treats them.
  useEffect(() => {
    if (isAi) navigate('/robi', { replace: true });
  }, [isAi, navigate]);

  const goBack = useCallback(() => {
    navigate(conversationId ? `/messages/${conversationId}` : '/messages');
  }, [navigate, conversationId]);

  const handleViewProfile = useCallback(async () => {
    if (resolvingPeer) return;
    setResolvingPeer(true);
    const guid = await resolvePeerUserGuid(conversation, selfUserId, getParticipants);
    setResolvingPeer(false);
    if (!guid) {
      ToastService.show({ type: 'error', message: t('profile.toast.unableToLoadProfile') });
      return;
    }
    navigate(`/profile/others/${guid}`);
  }, [resolvingPeer, conversation, selfUserId, getParticipants, navigate, t]);

  const handleReport = useCallback(async () => {
    if (resolvingPeer) return;
    setResolvingPeer(true);
    const guid = await resolvePeerUserGuid(conversation, selfUserId, getParticipants);
    setResolvingPeer(false);
    if (!guid) {
      ToastService.show({ type: 'error', message: t('profile.toast.unableToLoadProfile') });
      return;
    }
    setReportGuid(guid);
  }, [resolvingPeer, conversation, selfUserId, getParticipants, t]);

  const handleCreateGroup = useCallback(() => {
    const peerId = conversation?.peerUserId;
    if (!peerId) {
      ToastService.show({ type: 'error', message: t('profile.toast.unableToLoadProfile') });
      return;
    }
    const initialMember: ConversationMember = {
      id: String(peerId),
      name: displayName,
      username: conversation?.username ?? null,
      avatar: avatarSrc,
    };
    navigate('/messages/group/new', { state: { initialMember } });
  }, [conversation?.peerUserId, conversation?.username, displayName, avatarSrc, navigate, t]);

  const handleConfirm = useCallback(async () => {
    if (!conversation || busy) return;
    setBusy(true);
    try {
      if (prompt === 'block') {
        await blockConversation(conversation.id, conversation.peerUserId);
      } else {
        await deleteConversation(conversation.id);
      }
      setPrompt(null);
      navigate('/messages', { replace: true });
    } catch (caught) {
      ToastService.show({
        type: 'error',
        message:
          caught instanceof Error && caught.message
            ? caught.message
            : t('common.errors.generic'),
      });
    } finally {
      setBusy(false);
    }
  }, [conversation, busy, prompt, blockConversation, deleteConversation, navigate, t]);

  if (!conversation) {
    return (
      <section className="chat-details" aria-label={t('chatProfileDetails.chatSettings')}>
        <div className="chat-details__topbar">
          <button
            type="button"
            className="chat-header__back"
            onClick={goBack}
            aria-label={t('home.notifications.accessibility.back')}
          >
            <ChevronLeftIcon size={24} />
          </button>
        </div>
        {loading || !requestedList ? (
          <SkeletonGroup className="chat-details__loading">
            <Skeleton shape="circle" className="chat-details__loading-avatar" />
            <Skeleton className="chat-details__loading-name" />
            <Skeleton className="chat-details__loading-row" />
            <Skeleton className="chat-details__loading-row" />
          </SkeletonGroup>
        ) : (
          <ListStatus
            variant="error"
            message={t('chat.errors.openFailed')}
            actionLabel={t('messagesScreen.messages')}
            onAction={() => navigate('/messages')}
          />
        )}
      </section>
    );
  }

  const members = conversation.members?.length ? conversation.members : fetchedMembers ?? [];

  return (
    <section className="chat-details" aria-label={headerTitle}>
      <div className="chat-details__topbar">
        <button
          type="button"
          className="chat-header__back"
          onClick={goBack}
          aria-label={t('home.notifications.accessibility.back')}
        >
          <ChevronLeftIcon size={24} />
        </button>
      </div>

      <div className="chat-details__identity">
        <div className="chat-details__avatar-wrap">
          {isGroup && !avatarSrc ? (
            <GroupAvatar
              name={headerTitle}
              avatar={conversation.avatar}
              members={members}
              variant="details"
            />
          ) : (
            <Avatar
              src={avatarSrc}
              name={headerTitle}
              username={conversation.username}
              seed={conversation.peerUserGuid ?? conversation.peerUserId ?? conversation.id}
              size="xl"
              className="chat-details__avatar"
            />
          )}
          {conversation.isOnline && <span className="chat-details__online-dot" aria-hidden />}
        </div>
        <h1 className="chat-details__name">{headerTitle}</h1>
        {conversation.isOnline && (
          <p className="chat-details__status">{t('chatProfileDetails.online')}</p>
        )}
      </div>

      {!isGroup && (
        <div className="chat-details__primary">
          <Button variant="ghost" onClick={() => { handleViewProfile().catch(() => undefined); }}>
            {t('chatProfileDetails.viewProfile')}
          </Button>
        </div>
      )}

      {isGroup ? (
        <section className="chat-details__section" aria-labelledby="chat-details-members">
          <h2 className="chat-details__section-title" id="chat-details-members">
            {t('createGroupChat.members')}
          </h2>
          {members.length === 0 ? (
            membersError ? (
              <ListStatus
                variant="error"
                layout="inline"
                message={membersError}
                onAction={retryMembers}
              />
            ) : fetchedMembers === null ? (
              <SkeletonGroup className="chat-details__loading">
                <Skeleton className="chat-details__loading-row" />
                <Skeleton className="chat-details__loading-row" />
              </SkeletonGroup>
            ) : (
              <ListStatus
                variant="empty"
                layout="inline"
                title={t('groupDetails.activity.noMembers')}
                onAction={retryMembers}
              />
            )
          ) : null}
          <ul className="chat-details__members">
            {members.map(member => {
              const memberGuid = normalizeUserGuid(member.profileUserGuid);
              const canOpenMember = Boolean(memberGuid);
              const handleOpenMember = () => requireAuth(() => openUserProfile(memberGuid));
              const memberName = getUsernameDisplay(member);
              const memberAvatar = (
                <Avatar
                  src={resolveAvatarSrc(member.avatar)}
                  name={memberName}
                  username={member.username}
                  seed={memberGuid ?? member.id}
                  size="md"
                />
              );
              return (
                <li key={member.id} className="chat-details__member">
                  {canOpenMember ? (
                    <button
                      className="chat-details__member-avatar-action"
                      type="button"
                      aria-label={t('home.accessibility.openUserProfile', { username: memberName })}
                      onClick={handleOpenMember}
                    >
                      {memberAvatar}
                    </button>
                  ) : (
                    memberAvatar
                  )}
                  {canOpenMember ? (
                    <button
                      className="chat-details__member-name-action"
                      type="button"
                      onClick={handleOpenMember}
                    >
                      {memberName}
                    </button>
                  ) : (
                    <span className="chat-details__member-name">{memberName}</span>
                  )}
                  {member.isAdmin && (
                    <span className="chat-details__member-badge">{t('createGroupChat.admin')}</span>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      ) : (
        <section className="chat-details__section" aria-labelledby="chat-details-settings">
          <h2 className="chat-details__section-title" id="chat-details-settings">
            {t('chatProfileDetails.chatSettings')}
          </h2>
          <button type="button" className="chat-details__row" onClick={handleCreateGroup}>
            <BuddiesIcon size={20} />
            <span>{t('chatProfileDetails.createGroup', { name: displayName })}</span>
          </button>
        </section>
      )}

      <section className="chat-details__section" aria-labelledby="chat-details-safety">
        <h2 className="chat-details__section-title" id="chat-details-safety">
          {t('chatProfileDetails.safety')}
        </h2>
        {!isGroup && (
          <>
            <button
              type="button"
              className="chat-details__row"
              disabled={resolvingPeer}
              onClick={() => { handleReport().catch(() => undefined); }}
            >
              <AlertCircleIcon size={20} />
              <span>{t('chatProfileDetails.reportUser')}</span>
            </button>
            <button
              type="button"
              className="chat-details__row"
              onClick={() => setPrompt('block')}
            >
              <BlockIcon size={20} />
              <span>{t('chatProfileDetails.blockUser')}</span>
            </button>
          </>
        )}
        <button
          type="button"
          className="chat-details__row chat-details__row--destructive"
          onClick={() => setPrompt('delete')}
        >
          <TrashIcon size={20} />
          <span>{t('chatProfileDetails.deleteChat')}</span>
        </button>
      </section>

      {prompt && (
        <ConfirmModal
          title={t(prompt === 'block' ? 'messagesScreen.blockTitle' : 'messagesScreen.deleteChatTitle')}
          message={
            prompt === 'block'
              ? t('messagesScreen.blockDescription')
              : t('messagesScreen.deleteChatDescription', { name: headerTitle })
          }
          confirmLabel={t(prompt === 'block' ? 'messagesScreen.blockConfirm' : 'common.buttons.delete')}
          cancelLabel={t('common.buttons.cancel')}
          mediaUrl={avatarSrc}
          loading={busy}
          onClose={() => setPrompt(null)}
          onConfirm={() => { handleConfirm().catch(() => undefined); }}
          showIcon
        />
      )}

      {reportGuid && (
        <ReportUserModal userGuid={reportGuid} onClose={() => setReportGuid(null)} />
      )}
    </section>
  );
}
